/**
 * 'Fora do grupo': the trailing window's posts outside `related_post_share`'s count, over `api`'s
 * `GET /campaigns/{id}/unrelated-posts` -- paged there (`page`/`page_size`, default 1/50); this
 * screen reads every page in turn ("Carregar mais") so the list it shows is always exactly the
 * posts the share counts as unrelated, never a first-50 sample of them.
 *
 * `RelevanceCard`'s own link is this route's only entry point; not discovered by `../routes.ts`'s
 * glob (that matches one `routes.ts` per screen directory), so `./routes.ts` includes
 * `outOfGroupRoute` in its own `routes` array instead of exporting a second `bundle`.
 *
 * No Figma frame draws this screen -- `65:2`'s "Revisar publicações fora do grupo" link names
 * only its destination's *purpose*, not its layout -- so it reuses the record area's own page
 * chrome and states rather than inventing one. `.ts`, not `.tsx`, like `./routes.ts` itself: every
 * element below is `createElement`, and the back link is a real anchor plus `navigate()`
 * (`../accounts/routes.ts`'s own breadcrumb does the same) rather than `<Link>`, whose typed
 * `to`/`params` correlation is JSX-only and does not survive `createElement`. `QueryStates` is
 * generic for the same JSX-only reason, so the loading/error/empty branching below is inlined
 * instead of reusing it through `createElement`.
 */
import { createElement, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { z } from 'zod';

import { IconHint } from '../../../chrome/hint';
import { Absent } from '../../../chrome/measure';
import { EmptyState, ErrorState, LoadingState } from '../../../chrome/state';
import { Page, PageHeader } from '../../../chrome/page';
import { useBreadcrumbLeaf } from '../../../chrome/shell';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { getLocale } from '../../../i18n';
import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import type { RouteModule } from '../../routes/contract';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';
import { RECORD_PATH, recordPath } from './paths';
import { outOfGroupPostSchema, unrelatedPostsSchema } from './api/schemas';
import { rm } from './recordMessages';

const CONTROL_PLANE_READ = 'control_plane.read';

export const OUT_OF_GROUP_PATH = `${RECORD_PATH}/fora-do-grupo`;

type OutOfGroupPost = z.infer<typeof outOfGroupPostSchema>;

type UnrelatedPosts = z.infer<typeof unrelatedPostsSchema>;

function useUnrelatedPostsPage(
  campaignId: string,
  page: number,
): UseQueryResult<UnrelatedPosts, ApiFailureError> {
  const client = useApiClient();
  return useQuery<UnrelatedPosts, ApiFailureError>({
    queryKey: ['campaign', campaignId, 'unrelated-posts', page],
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.unrelatedPosts, { campaignId }, { page: String(page) }),
        GET_ROUTES.unrelatedPosts.schema,
      ),
    enabled: campaignId !== '',
  });
}

/** `presence.platform.*` labels this record area already carries the row uses, not the raw wire
 *  enum -- a value the vocabulary has not caught up to still reads as something, never a crash. */
function platformLabel(platform: string): string {
  if (platform === 'instagram') return rm('presence.platform.instagram');
  if (platform === 'tiktok') return rm('presence.platform.tiktok');
  return platform;
}

function outOfGroupRow(post: OutOfGroupPost): ReactNode {
  const author = post.label ?? post.handle;
  const publishedAt = new Intl.DateTimeFormat(getLocale(), { dateStyle: 'short' }).format(
    new Date(post.published_at),
  );
  const meta = [author, platformLabel(post.platform), publishedAt]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  return createElement(
    'li',
    {
      key: post.post_id,
      className:
        'flex items-center justify-between gap-3 rounded-md border bg-surface-brief px-3 py-2',
    },
    createElement(
      'div',
      { className: 'flex min-w-0 flex-col gap-1' },
      createElement(
        'p',
        { className: 'truncate text-body-sm text-foreground' },
        post.caption !== null
          ? post.caption
          : createElement(Absent, { reason: { text: rm('outOfGroup.post.noCaption') } }),
      ),
      createElement(
        'p',
        { className: 'truncate text-caption text-muted-foreground' },
        meta !== '' ? meta : createElement(Absent, { reason: 'not_observable' }),
      ),
    ),
    post.deleted_at !== null &&
      createElement(
        Badge,
        { variant: 'outline', className: 'shrink-0' },
        rm('outOfGroup.post.deleted'),
      ),
    post.url !== null &&
      createElement(IconHint, {
        label: rm('outOfGroup.post.open'),
        children: createElement(
          'a',
          {
            href: post.url,
            target: '_blank',
            rel: 'noreferrer',
            className: 'shrink-0 text-muted-foreground hover:text-foreground',
          },
          createElement(ExternalLink, { 'aria-hidden': true, className: 'size-3.5' }),
        ),
      }),
  );
}

/** Loading, then error, then empty, then rows -- same order and reasoning as `QueryStates`
 *  (`../../../chrome/state/QueryStates.tsx`), inlined for the reason the module doc gives. Every
 *  page fetched so far is appended, never replaced, so "Carregar mais" never re-shows a row. A
 *  later page's failure renders below the rows it leaves standing, so the list never silently
 *  stops short of `total`. */
function OutOfGroupBody(props: {
  query: UseQueryResult<UnrelatedPosts, ApiFailureError>;
  rows: OutOfGroupPost[];
  total: number;
  onLoadMore: () => void;
}): ReactNode {
  const { query, rows, total } = props;
  if (query.status === 'pending' && rows.length === 0) return createElement(LoadingState);
  if (query.status === 'error' && rows.length === 0) {
    return createElement(ErrorState, {
      failure: query.error.failure,
      onRetry: () => void query.refetch(),
    });
  }
  if (rows.length === 0) {
    return createElement(EmptyState, { title: rm('outOfGroup.empty.title') });
  }
  return createElement(
    'div',
    { className: 'flex flex-col gap-3' },
    createElement('ul', { className: 'flex flex-col gap-2' }, rows.map(outOfGroupRow)),
    query.status === 'error' &&
      createElement(ErrorState, {
        failure: query.error.failure,
        onRetry: () => void query.refetch(),
      }),
    query.status !== 'error' &&
      rows.length < total &&
      createElement(
        Button,
        {
          type: 'button',
          variant: 'outline',
          disabled: query.status === 'pending',
          onClick: props.onLoadMore,
        },
        rm('outOfGroup.loadMore'),
      ),
  );
}

export function OutOfGroupPosts(props: { campaignId: string }): ReactNode {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<OutOfGroupPost[]>([]);
  // Held across pages: while page N is pending or failed, `query.data` is undefined.
  const [total, setTotal] = useState(0);
  const query = useUnrelatedPostsPage(props.campaignId, page);
  useBreadcrumbLeaf(rm('outOfGroup.title'));

  // `query.data` itself is deliberately not a dependency: react-query hands back a new identity
  // on every background refetch of the *same* page, which would otherwise re-append that page's
  // rows a second time. `query.status` turning 'success' for a new `page` is the one edge that
  // should append.
  useEffect(() => {
    if (query.status !== 'success') return;
    setTotal(query.data.total);
    setRows((previous) => (page === 1 ? query.data.rows : [...previous, ...query.data.rows]));
  }, [query.status, page]);

  return createElement(
    Page,
    null,
    createElement(
      'a',
      {
        href: recordPath(props.campaignId),
        className:
          'flex w-fit items-center gap-1 text-body-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline',
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          void navigate({ to: RECORD_PATH, params: { campaignId: props.campaignId } });
        },
      },
      createElement(ChevronLeft, { 'aria-hidden': true, className: 'size-3.5' }),
      rm('outOfGroup.back'),
    ),
    createElement(PageHeader, { title: rm('outOfGroup.title') }),
    createElement(OutOfGroupBody, {
      query,
      rows,
      total,
      onLoadMore: () => setPage((current) => current + 1),
    }),
  );
}

function useParamCampaignId(): string {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  return campaignId ?? '';
}

function OutOfGroupRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  return createElement(OutOfGroupPosts, { campaignId });
}

/** `./routes.ts`'s own `bundle` spreads this into its `routes` array -- see the module doc for
 *  why it is not a second `bundle` export of its own. */
export const outOfGroupRoute: RouteModule = {
  path: OUT_OF_GROUP_PATH,
  element: createElement(OutOfGroupRoute),
  requires: CONTROL_PLANE_READ,
  kind: 'data',
};
